import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertContext } from "@actinc/dls/context";
import { BankedItemSaveDialog } from "../../components/BankedItemSaveDialog";
import {
  ChevronDown as ChevronDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@actinc/dls/icons";
import { searchTasksByMetadataQuery } from "../Search/Tasks";
import {
  AppBar,
  Box,
  Button,
  ButtonGroup,
  DialogContentText,
  List,
  ListItem,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { Responsive, WidthProvider } from "react-grid-layout";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import {
  Link as RouterLink,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useClient } from "urql";

import {
  AlertDialog,
  Backdrop,
  WarningDialog,
  WorkspaceCard,
} from "../../components";

import { MetadataConfigurationContext } from "../../metadataConfigurationProvider";
import { ProgramsContext } from "../../programsProvider";
import { UsersContext } from "../../usersProvider";

import {
  filterOptionsByObjectValues,
  normalizeConfiguration,
  transformConfigToSchema,
  validateWorkspaceValues,
} from "../../services/formUtils";
import { getCharacterCount } from "../../services/contentUtils";
import { useSessionStore, useStore } from "../../services/hooks";
import { getSaveWarnings, hasUndefinedFoilContent } from "../../services/item";
import { clearWorkspaceState, getState } from "../../services/sessionState";
import {
  canPerformAction,
  permissionsInclude,
  setTitle,
} from "../../services/utils";
import { getElaMinHeight } from "../../stores/workspace";

import { AssetsCard } from "./AssetsCard";
import { CardList } from "./CardList";
import { ItemContentCard } from "./ItemContentCard";
import { ItemSetSummaryCard } from "./ItemSetSummaryCard";
import { MetadataCard } from "./MetadataCard";
import { PassageContentCard } from "./PassageContentCard";
import { PassageReferenceContentCard } from "./PassageReferenceContentCard";
import { PreviewCard } from "./PreviewCard";
import { TasksCard } from "./TasksCard";
import { UserContext } from "../../userProvider";
import { ReadyProvider } from "../../providers/ReadyProvider";
import { ViewItemImageAssetsCard } from "./ViewItemImageAssetsCard";
import { ViewPassageImageAssetsCard } from "./ViewPassageImageAssetsCard";
import { useTTSFileStatusContext } from "../../providers/TTSFileStatusProvider";
import { useTtsStatusReconciler } from "./hooks/useTtsStatusReconciler";
import { getTTSTagIdsToRegenerate as computeTTSTagIdsToRegenerate } from "./getTTSTagIdsToRegenerate";

const ResponsiveGridLayout = WidthProvider(Responsive);

const Workspace = () => {
  const user = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const itemID = searchParams.get("itemID");
  const passageID = searchParams.get("passageID");
  const client = useClient();

  const alertContext = useContext(AlertContext);
  const programs = useContext(ProgramsContext);
  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const users = useContext(UsersContext);

  const location = useLocation();

  const [alertDialogProps, setAlertDialogProps] = useState({
    open: false,
  });
  const [disableSwitchItem, setDisableSwitchItem] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const isBankedSubmiting = useRef(false);
  const [itemSetNavigationMenuAnchorEl, setItemSetNavigationMenuAnchorEl] =
    useState();
  const [program, setProgram] = useState();
  const [saveIssues, setSaveIssues] = useState([]);
  const [showAccessError, setShowAccessError] = useState(false);
  const [showBackDrop, setShowBackDrop] = useState(false);
  const [loading, setLoading] = useState(() => ({
    item: !!itemID,
    passage: !!passageID,
  }));
  const [warningDialogProps, setWarningDialogProps] = useState({
    open: false,
  });

  const [areComponentsNotReady, setAreComponentsNotReady] = useState(true);
  const hasHandledCallback = useRef(false);
  const prevUndefinedFoilDetected = useRef(false);
  const [undefinedFoilDetected, setUndefinedFoilDetected] = useState(false);

  const [item, itemStore] = useStore("item");
  const [passage, passageStore] = useStore("passage");

  const [workspace, workspaceStore] = useSessionStore("workspace");

  const stopSubscriptionRef = useRef(null);
  const { startSubscription, ttsTagStatusMap } = useTTSFileStatusContext();

  const formMethods = useForm({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldUseNativeValidation: false,
  });
  const { control, getValues, handleSubmit, reset, setValue, watch } =
    formMethods;
  const [formItemID, formPassageID, formPassageContentArea] = watch([
    "item.id",
    "passage.id",
    "passage.contentArea",
  ]);

  const { dirtyFields, isDirty, isSubmitting } = useFormState({
    control,
  });

  const [userTasks, setUserTasks] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const promises = (passage?.items || []).map(({ name }) =>
        client
          .query(searchTasksByMetadataQuery, {
            assignee: user.id,
            itemName: name,
            program: passage.program?.id,
            rowLimit: 25,
            taskState: "Open",
          })
          .toPromise(),
      );
      const response = await Promise.all(promises);
      if (response) {
        const newTasks = response.flatMap(
          (item) => item.data?.tasksByMetadata || [],
        );
        setUserTasks(newTasks);
      }
    };
    fetchData();
  }, [passage?.items]);

  const activeTasks = useMemo(() => {
    if (getState("lastSearchLocation") !== "/")
      return { item: null, passage: null };
    return {
      item:
        [...(item?.tasks || [])]
          .reverse()
          .find((task) => task._actions.includes("edit")) || null,
      passage:
        [...(passage?.tasks || [])]
          .reverse()
          .find((task) => task._actions.includes("edit")) || null,
    };
  }, [item?.tasks, passage?.tasks]);

  const disableItemsInMenu = useMemo(
    () =>
      ["item", "passage"].some((objectType) =>
        permissionsInclude({ activeTasks, type: objectType })("externalReview"),
      ),
    [activeTasks],
  );

  const displayProgram = useMemo(
    () =>
      !["item", "passage"].some((objectType) =>
        permissionsInclude({ activeTasks, type: objectType })("externalReview"),
      ),
    [activeTasks],
  );

  const inWorkflow = useMemo(
    () =>
      !!["item", "passage"].filter((objectType) => activeTasks[objectType])
        .length,
    [activeTasks],
  );

  const accessibleItems = useMemo(
    () =>
      (passage?.items || []).filter(
        (i) =>
          !disableItemsInMenu ||
          userTasks.some((task) => task.item?.id === i.id),
      ),
    [passage?.items, disableItemsInMenu, userTasks],
  );

  // If the passage or item ID change, determine the item index in the set
  const index = useMemo(
    () =>
      accessibleItems && itemID
        ? accessibleItems.map((i) => i.id).indexOf(itemID)
        : null,
    [accessibleItems, itemID],
  );
  const itemAdminAccess = useMemo(() => {
    if (!item?.program?.id || !user?.programAccess?.length) return false;

    return (
      !user.superAdmin &&
      user.programAccess.some(
        (program) =>
          program.program === item.program.id &&
          item.developmentState === "Banked" &&
          !program.permissions.includes("ProgramAdmin") &&
          program.reviewTypes.includes("Content Specialist"),
      )
    );
  }, [item?.program, user]);

  const passageAdminAccess = useMemo(() => {
    if (!passage?.program?.id || !user?.programAccess?.length) return false;

    return (
      !user.superAdmin &&
      user.programAccess.some(
        (program) =>
          program.program === passage.program.id &&
          passage.developmentState === "Banked" &&
          !program.permissions.includes("ProgramAdmin") &&
          program.reviewTypes.includes("Content Specialist"),
      )
    );
  }, [passage?.program, user]);

  const elaMinHeight = useMemo(
    () =>
      inWorkflow && getElaMinHeight({ item, itemID, passage, passageID, user }),
    [inWorkflow, item, itemID, passage, passageID, user],
  );

  const itemConfiguration = useMemo(() => {
    if (
      !item?.program?.configurationID ||
      !metadataConfiguration ||
      !metadataConfiguration.item?.length ||
      !users?.length
    )
      return null;
    return normalizeConfiguration("item")(metadataConfiguration)(users)(
      item?.program,
    );
  }, [metadataConfiguration, item?.program, users]);

  const itemJoiSchema = useMemo(() => {
    return transformConfigToSchema(itemConfiguration);
  }, [itemConfiguration]);

  const passageConfiguration = useMemo(() => {
    if (
      !passage?.program?.configurationID ||
      !metadataConfiguration ||
      !metadataConfiguration.passage?.length ||
      !users?.length
    )
      return null;
    return normalizeConfiguration("passage")(metadataConfiguration)(users)(
      passage?.program,
    );
  }, [metadataConfiguration, passage?.program, users]);

  const passageJoiSchema = useMemo(() => {
    return transformConfigToSchema(passageConfiguration);
  }, [passageConfiguration]);

  // Outside a workflow (opened from Search) only admins may edit the item.
  const canEditItem = useMemo(
    () => canPerformAction({ inWorkflow, user })(item)("edit"),
    [inWorkflow, item, user],
  );

  const readonly = useMemo(
    () => !(inWorkflow || canEditItem || passage?._actions.includes("edit")),
    [canEditItem, inWorkflow, passage?._actions],
  );

  const cards = useMemo(() => {
    return [
      {
        component: (
          <CardList activeTasks={activeTasks} useMinHeight={elaMinHeight} />
        ),
        key: "CardList",
      },
      {
        canClose: true,
        component: item && (
          <ItemContentCard
            activeTasks={activeTasks}
            readonly={
              (inWorkflow &&
                permissionsInclude({ activeTasks, type: "item" })(
                  "readonly",
                )) ||
              (!inWorkflow && !canEditItem)
            }
          />
        ),
        key: "ItemContentCard",
        title: "Item Content",
      },
      {
        canClose: true,
        component: item && (
          <PreviewCard activeTasks={activeTasks} objectType="item" />
        ),
        key: "ItemPreviewCard",
        title: "Item Preview",
      },
      {
        canClose: true,
        component: item && (
          <MetadataCard
            activeTasks={activeTasks}
            configuration={itemConfiguration}
            objectType="item"
            readonly={
              (inWorkflow &&
                (permissionsInclude({ activeTasks, type: "item" })(
                  "readonly",
                ) ||
                  permissionsInclude({ activeTasks, type: "item" })(
                    "readonlyItemMetadata",
                  ))) ||
              (!inWorkflow && !canEditItem)
            }
          />
        ),
        key: "ItemMetadataCard",
        title: "Item Metadata",
      },
      {
        canClose: true,
        component: item && (
          <AssetsCard
            activeTasks={activeTasks}
            allObjectTasks={item.tasks}
            objectType="item"
            readonly={
              (inWorkflow &&
                (permissionsInclude({ activeTasks, type: "item" })(
                  "readonly",
                ) ||
                  permissionsInclude({ activeTasks, type: "item" })(
                    "readonlyItemAssets",
                  ))) ||
              (!inWorkflow && !canEditItem)
            }
          />
        ),
        key: "ItemAssetsCard",
        title: "Item Assets",
      },
      {
        canClose: true,
        component: item && (
          <ViewItemImageAssetsCard
            activeTasks={activeTasks}
            allObjectTasks={item.tasks}
            objectType="item"
            readonly={
              (inWorkflow &&
                (permissionsInclude({ activeTasks, type: "item" })(
                  "readonly",
                ) ||
                  permissionsInclude({ activeTasks, type: "item" })(
                    "readonlyItemAssets",
                  ))) ||
              (!inWorkflow && !canEditItem)
            }
          />
        ),
        key: "ViewItemImageAssetsCard",
        title: "View Item Image Assets",
      },
      {
        canClose: true,
        component: item && (
          <TasksCard activeTasks={activeTasks} objectType="item" />
        ),
        key: "ItemTasksCard",
        title: "Item Tasks",
      },
      {
        canClose: true,
        component: passage && (
          <PassageContentCard
            activeTasks={activeTasks}
            readonly={
              (inWorkflow &&
                permissionsInclude({ activeTasks, type: "passage" })(
                  "readonly",
                )) ||
              (!inWorkflow && !passage?._actions.includes("edit"))
            }
          />
        ),
        key: "PassageContentCard",
        title: "Passage Content",
      },
      {
        canClose: true,
        component: passage && (
          <PreviewCard activeTasks={activeTasks} objectType="passage" />
        ),
        key: "PassagePreviewCard",
        title: "Passage Preview",
      },
      {
        canClose: true,
        component: passage && (
          <MetadataCard
            activeTasks={activeTasks}
            configuration={passageConfiguration}
            objectType="passage"
            readonly={
              (inWorkflow &&
                (permissionsInclude({ activeTasks, type: "passage" })(
                  "readonly",
                ) ||
                  permissionsInclude({ activeTasks, type: "passage" })(
                    "readonlyPassageMetadata",
                  ))) ||
              (!inWorkflow && !passage?._actions.includes("edit"))
            }
          />
        ),
        key: "PassageMetadataCard",
        title: "Passage Metadata",
      },
      {
        canClose: true,
        component: passage && (
          <ItemSetSummaryCard
            activeTasks={activeTasks}
            allPrograms={programs}
            userTasks={userTasks}
            metadataConfiguration={metadataConfiguration}
            readonly={
              (inWorkflow &&
                (permissionsInclude({ activeTasks, type: "passage" })(
                  "readonly",
                ) ||
                  permissionsInclude({ activeTasks, type: "passage" })(
                    "readonlyItemSet",
                  ))) ||
              (!inWorkflow && !passage?._actions.includes("edit"))
            }
          />
        ),
        key: "ItemSetSummaryCard",
        title: "Item Set Summary",
      },
      {
        canClose: true,
        component: passage && (
          <AssetsCard
            activeTasks={activeTasks}
            allObjectTasks={passage.tasks}
            objectType="passage"
            readonly={
              (inWorkflow &&
                (permissionsInclude({ activeTasks, type: "passage" })(
                  "readonly",
                ) ||
                  permissionsInclude({ activeTasks, type: "passage" })(
                    "readonlyPassageAssets",
                  ))) ||
              (!inWorkflow && !passage?._actions.includes("edit"))
            }
          />
        ),
        key: "PassageAssetsCard",
        title: "Passage Assets",
      },
      {
        canClose: true,
        component: passage && (
          <ViewPassageImageAssetsCard
            activeTasks={activeTasks}
            allObjectTasks={passage.tasks}
            objectType="passage"
            readonly={
              (inWorkflow &&
                (permissionsInclude({ activeTasks, type: "passage" })(
                  "readonly",
                ) ||
                  permissionsInclude({ activeTasks, type: "passage" })(
                    "readonlyPassageAssets",
                  ))) ||
              (!inWorkflow && !passage?._actions.includes("edit"))
            }
          />
        ),
        key: "ViewPassageImageAssetsCard",
        title: "View Passage Image Assets",
      },
      {
        canClose: true,
        component: passage && (
          <PassageReferenceContentCard
            activeTasks={activeTasks}
            readonly={
              (inWorkflow &&
                permissionsInclude({ activeTasks, type: "passage" })(
                  "readonly",
                )) ||
              (!inWorkflow && !passage?._actions.includes("edit"))
            }
          />
        ),
        key: "PassageReferenceContentCard",
        title: "Passage Reference Content",
      },
      {
        canClose: true,
        component: passage && (
          <TasksCard activeTasks={activeTasks} objectType="passage" />
        ),
        key: "PassageTasksCard",
        title: "Passage Tasks",
      },
    ].filter(
      (card) =>
        card.key !== "PassageReferenceContentCard" ||
        formPassageContentArea === "English",
    );
  }, [
    activeTasks,
    canEditItem,
    formPassageContentArea,
    item?._actions,
    itemConfiguration,
    metadataConfiguration,
    passageConfiguration,
    passage?._actions,
    programs,
    userTasks,
  ]);

  // Fetch the item data when the itemID changes
  useEffect(() => {
    if (!itemStore.client) itemStore.init(client);
    if (!programs?.length) return;
    if (itemID !== item?.id) {
      setLoading((prev) => ({ ...prev, item: true }));
      async function getItem() {
        await itemStore.fetch(users)(programs)(itemID);
        setLoading((prev) => ({ ...prev, item: false }));
      }
      getItem();
    }
  }, [client, item?.id, itemID, programs, users]);

  // Fetch the passage data when the passageID changes
  useEffect(() => {
    if (!passageStore.client) passageStore.init(client);
    if (!programs?.length) return;
    if (passageID !== passage?.id) {
      setLoading((prev) => ({ ...prev, passage: true }));
      async function getPassage() {
        await passageStore.fetch(users)(programs)(passageID);
        setLoading((prev) => ({ ...prev, passage: false }));
      }
      getPassage();
    }
  }, [client, passage?.id, passageID, programs]);

  // React to updates to the item store
  useEffect(() => {
    !program && item?.program && setProgram(item.program);
    if (getValues().item?.hasOwnProperty("type")) {
      const priorItemType = getValues().item.type;
      if (priorItemType !== item?.type) {
        setWarningDialogProps({
          message: (
            <DialogContentText>
              {`Item Type was changed from invalid value: "${priorItemType}" to default value: "${item?.type}"`}
            </DialogContentText>
          ),
          onClose: () => setWarningDialogProps({ open: false }),
          open: true,
          title: `Warning: Type Value Has Changed`,
        });
      }
    }
    if (itemID && item === null && !loading.item) {
      !showAccessError && setShowAccessError(true);
    } else {
      setFormData(getValues())("item")(item);
    }
  }, [item, loading.item]);

  // React to updates to the passage store
  useEffect(() => {
    !program && passage?.program && setProgram(passage.program);
    if (passageID && passage === null && !loading.passage) {
      !showAccessError && setShowAccessError(true);
    } else {
      setFormData(getValues())("passage")(passage);
    }
  }, [passage, loading.passage]);

  useTtsStatusReconciler({
    client,
    user,
    item,
    passage,
    itemId: itemID,
    passageId: passageID,
    itemStore,
    passageStore,
    programs,
    users,
    startSubscription,
    ttsTagStatusMap,
    setValue,
    stopSubscriptionRef,
  });

  useEffect(() => {
    showAccessError &&
      alertContext.actions.addAlert({
        message: `You don't have permission to this content. Please contact your administrator.`,
        options: { persist: true, variant: "error" },
      });
  }, [showAccessError]);

  // React to updates to the workspace store
  useEffect(() => {}, [workspace]);

  useEffect(() => {
    if (
      !((itemID && !item?.id) || (passageID && !passage?.id)) &&
      !workspace?.cards.length
    )
      workspaceStore.setLayout({
        activeTasks,
        item: !!itemID,
        passage: !!passageID,
        useMinHeight: elaMinHeight,
      });
  }, [activeTasks, item, !!itemID, passage, !!passageID, workspace]);

  // Handle the case where the form is dirty and the user is moving away from the page
  useEffect(() => {
    let disableSaveButton = false;
    // the dirtyFields array may actually be empty, check in order to determine
    // if there have actually been changes
    if (!isDirty && Object.keys(dirtyFields).length) {
      disableSaveButton = shouldDisableSave({
        actions: [...(item?._actions || []), ...(passage?._actions || [])],
        readonly,
      })(dirtyFields);
    }

    if (!!Object.keys(dirtyFields).length && !disableSaveButton) {
      console.info("Adding preventUnload listener");
      window.addEventListener("beforeunload", preventUnload);
      return () => window.removeEventListener("beforeunload", preventUnload);
    } else {
      console.info("Removing preventUnload listener");
      window.removeEventListener("beforeunload", preventUnload);
    }
  }, [dirtyFields, isDirty]);

  // Subscribe to interaction field changes to detect undefined foil content
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.startsWith("item.interactions")) {
        const detected = hasUndefinedFoilContent(value?.item?.interactions);
        setUndefinedFoilDetected(detected);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Show alert when undefined foil content is first detected
  useEffect(() => {
    if (undefinedFoilDetected && !prevUndefinedFoilDetected.current) {
      alertContext.actions.addAlert({
        message:
          'A foil contains invalid content ("undefined"). Please correct it before saving.',
        options: { variant: "error" },
      });
    }
    prevUndefinedFoilDetected.current = undefinedFoilDetected;
  }, [undefinedFoilDetected]);

  // Determine whether buttons for saving and switching items should be disabled
  useEffect(() => {
    console.info("Determining whether to disable item switch and/or save");
    let disableSaveButton = false;
    // the dirtyFields array may actually be empty, check in order to determine
    // if there have actually been changes
    if (!isDirty && Object.keys(dirtyFields).length) {
      disableSaveButton = shouldDisableSave({
        actions: [...(item?._actions || []), ...(passage?._actions || [])],
        readonly,
      })(dirtyFields);
    }

    if (hasHandledCallback.current) {
      hasHandledCallback.current = false;
      return;
    }

    const { itemOverride, passageOverride } = hasMetafileOnlySaveOverride({
      item,
      passage,
      itemAdminAccess,
      passageAdminAccess,
      dirtyFields,
    });

    setDisableSave(
      (itemAdminAccess && !itemOverride) ||
        (passageAdminAccess && !passageOverride) ||
        isSubmitting ||
        !Object.keys(dirtyFields).length ||
        disableSaveButton ||
        areComponentsNotReady ||
        undefinedFoilDetected,
    );

    setDisableSwitchItem(
      isSubmitting ||
        (!!Object.keys(dirtyFields).length && !disableSaveButton) ||
        index < 0,
    );
  }, [
    dirtyFields,
    isDirty,
    isSubmitting,
    item,
    passage,
    index,
    areComponentsNotReady,
    undefinedFoilDetected,
    itemAdminAccess,
    passageAdminAccess,
    readonly,
  ]);

  useEffect(() => {
    setTitle(["CDE", passage?.name, item?.name]);
  }, [item, passage]);

  const getQueryString = (direction) => {
    return (
      "?" +
      ["itemID", "passageID"]
        .reduce((acc, key) => {
          if (key === "passageID") {
            return passageID
              ? [...acc, `${key}=${passageID}`]
              : [...acc, false];
          }
          if (key === "itemID") {
            if (!passage?.items.length || !accessibleItems.length)
              return [...acc, false];
            if (direction === "next")
              return [
                ...acc,
                `${key}=${
                  index !== null && index + 1 < accessibleItems.length
                    ? accessibleItems[index + 1].id
                    : accessibleItems[0].id
                }`,
              ];
            if (direction === "previous")
              return [
                ...acc,
                `${key}=${
                  index !== null && index - 1 >= 0
                    ? accessibleItems[index - 1].id
                    : accessibleItems[accessibleItems.length - 1].id
                }`,
              ];
          }
        }, [])
        .filter(Boolean)
        .join("&")
    );
  };
  const goBack = () => {
    clearWorkspaceState();
    if (window.name) return window.close();

    // note originally using navigate but it failed to display the are you sure? message to the user
    window.location.assign(getState("lastSearchLocation") || "/", {
      state: { from: location.pathname },
    });
  };
  const handleItemSetNavigationMenuClick = (e) => {
    setItemSetNavigationMenuAnchorEl(e.currentTarget);
  };
  const handleItemSetNavigationMenuClose = () => {
    setItemSetNavigationMenuAnchorEl(null);
  };

  const [saveBankedProps, setSaveBankedProps] = useState({
    open: false,
  });

  const onSubmit = async (data) => {
    const values = getValues();
    const validationErrors = validateWorkspaceValues({
      item: itemJoiSchema,
      passage: passageJoiSchema,
    })(values);

    if (validationErrors.length)
      return openValidationErrorDialog(validationErrors);

    const issues = [
      getSaveWarnings(data.item)(data.passage)(inWorkflow)(user),
    ].flat();

    if (!issues.length) {
      return onSubmitBanked({ ...data });
    }
    setSaveIssues(issues);
    setAlertDialogProps({
      dialogTitle: `Confirm Save`,
      maxWidth: "sm",
      onClose: (save) => {
        setAlertDialogProps({ open: false });
        save && onSubmitBanked({ ...data });
      },
      open: true,
    });
  };

  const onSubmitBanked = async (data) => {
    const values = getValues();
    const isItemDirty = hasDirtyLeaf(dirtyFields?.item);
    const isPassageDirty = hasDirtyLeaf(dirtyFields?.passage);
    const shouldPromptForBankedComment =
      (values.item?.developmentState === "Banked" && isItemDirty) ||
      (values.passage?.developmentState === "Banked" && isPassageDirty);

    if (shouldPromptForBankedComment) {
      return setSaveBankedProps({
        onClose: (notes) => {
          setSaveBankedProps({ open: false });
          isBankedSubmiting.current = notes ? true : false;
          notes && saveWorkspaceData({ ...data, notes });
        },
        open: true,
      });
    }
    return saveWorkspaceData({ ...data });
  };

  const openValidationErrorDialog = (validationErrors) => {
    setWarningDialogProps({
      message: (
        <div>
          <p>Cannot save due to the following errors: </p>
          <List dense={true} disablePadding={true}>
            <ListItem></ListItem>
            {validationErrors.map((error) => {
              return (
                <ListItem dense={true} key={error}>
                  {error}
                </ListItem>
              );
            })}
          </List>
        </div>
      ),
      onClose: () => setWarningDialogProps({ open: false }),
      open: true,
      title: `Invalid fields exist`,
    });
  };

  const getTTSToogleClicked = (data) => {
    let itemTTSGenerate = false;
    let passageTTSGenerate = false;

    if (data.item && "generateTTS" in data.item) {
      itemTTSGenerate = data.item.generateTTS;
      delete data.item.generateTTS;
    }

    if (data.passage && "generateTTS" in data.passage) {
      passageTTSGenerate = data.passage.generateTTS;

      delete data.passage.generateTTS;
    }

    return { itemTTSGenerate, passageTTSGenerate };
  };

  const getTTSTagIdsToRegenerate = (objectType, ttsGenerate, nextData) =>
    computeTTSTagIdsToRegenerate({
      ttsGenerate,
      nextData,
      savedObj: objectType === "item" ? itemStore.get() : passageStore.get(),
      liveMap: ttsTagStatusMap[nextData?.id] ?? {},
    });

  const saveWorkspaceData = async (data) => {
    setShowBackDrop(true);

    const { itemTTSGenerate, passageTTSGenerate } = getTTSToogleClicked(data);

    const isItemDirty = item && Object.keys(dirtyFields ?? {}).includes("item");
    const isPassageDirty =
      passage && Object.keys(dirtyFields ?? {}).includes("passage");
    const shouldSavePassage = passage && (isPassageDirty || isItemDirty);

    const updatedItemPbtCharacterCount =
      isItemDirty && data?.item
        ? (() => {
            const { interactions = [], ...itemData } = data.item;

            const separatedInteractions = {
              choiceInteractions: interactions.filter(
                (i) => i.__typename === "ChoiceInteraction",
              ),
              gapMatchInteractions: interactions.filter(
                (i) => i.__typename === "GapMatchInteraction",
              ),
              inlineChoiceInteractions: interactions.filter(
                (i) => i.__typename === "InlineChoiceInteraction",
              ),
              matchInteractions: interactions.filter(
                (i) => i.__typename === "MatchInteraction",
              ),
              orderInteractions: interactions.filter(
                (i) => i.__typename === "OrderInteraction",
              ),
            };

            return getCharacterCount("pbt")({
              ...itemData,
              ...separatedInteractions,
            });
          })()
        : null;

    const applyUpdatedItemPbtCharacterCount = (passageData) => {
      if (
        !isItemDirty ||
        !passageData?.items?.length ||
        !data?.item?.id ||
        updatedItemPbtCharacterCount === null
      ) {
        return passageData;
      }

      return {
        ...passageData,
        items: passageData.items.map((passageItem) => {
          if (!passageItem) return passageItem;
          const itemId =
            typeof passageItem === "string" ? passageItem : passageItem.id;
          if (itemId !== data.item.id) return passageItem;
          if (typeof passageItem === "string") return passageItem;
          return {
            ...passageItem,
            pbtCharacterCount: updatedItemPbtCharacterCount,
          };
        }),
      };
    };

    const itemTTSTagIdsToRegenerate = getTTSTagIdsToRegenerate(
      "item",
      itemTTSGenerate,
      data.item,
    );
    const passageData = applyUpdatedItemPbtCharacterCount(data.passage);
    const passageTTSTagIdsToRegenerate = getTTSTagIdsToRegenerate(
      "passage",
      passageTTSGenerate,
      passageData,
    );

    const requestIds = [data.item?.id, data.passage?.id].filter(Boolean);

    const unsubscribeFunc = await startSubscription(client)(user.id)(
      requestIds,
    );
    stopSubscriptionRef.current = unsubscribeFunc;

    // Item save must complete before passage save so the server's passage response
    // reflects the committed item pbtCharacterCount when recalculating averageStandardWordCount.
    try {
      if (isItemDirty) {
        await itemStore.save(users)(programs)(data.notes)({ activeTasks })(
          filterOptionsByObjectValues(getValues().item)(itemConfiguration)(
            itemConfiguration["type"].options,
          ),
        )(itemTTSGenerate)(itemTTSTagIdsToRegenerate)(data.item);
      }
      if (shouldSavePassage) {
        await passageStore.save(users)(programs)(data.notes)({ activeTasks })(
          passageTTSGenerate,
        )(passageTTSTagIdsToRegenerate)({})(passageData);
      }
      if (isBankedSubmiting.current) {
        item && (await itemStore.fetch(users)(programs)(itemID));
        passage && (await passageStore.fetch(users)(programs)(passageID));
        isBankedSubmiting.current = false;
      }
      setShowBackDrop(false);
      alertContext.actions.addAlert({
        message: `Save successful!`,
        options: { variant: "success" },
      });
      setValue("item.generateTTS", false, { shouldDirty: false });
      setValue("passage.generateTTS", false, { shouldDirty: false });
      validateIfDirtyFieldsAfterSaveForm();
    } catch {
      setShowBackDrop(false);
      alertContext.actions.addAlert({
        message: `Save failed!`,
        options: { persist: true, variant: "error" },
      });
    }
  };

  const validateIfDirtyFieldsAfterSaveForm = () => {
    if (isDirty || Object.keys(dirtyFields ?? {}).length) {
      reset(getValues(), { keepValues: true });
    }
  };

  const setFormData =
    (formData) =>
    (key) =>
    (o = {}) => {
      reset({ ...formData, [key]: { ...o } });
    };

  const handleAllReady = () => {
    setAreComponentsNotReady(false);
  };

  if (!workspace) return null;

  return (
    <ReadyProvider onAllReady={handleAllReady}>
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onSubmit, onSubmit)}
          noValidate
          onKeyDown={(e) => {
            if (
              e.code === "Enter" &&
              e.target.tagName === "INPUT" &&
              e.target.type !== "submit" &&
              e.target.type !== "button"
            )
              e.preventDefault();
          }}
        >
          <Backdrop
            loading={showBackDrop || isSubmitting || isBankedSubmiting.current}
          />
          {(!itemID || formItemID) && (!passageID || formPassageID) && (
            <>
              <AppBar
                color={readonly ? "default" : "secondary"}
                position="sticky"
                sx={{ zIndex: "drawer" }}
              >
                <Toolbar
                  sx={{
                    justifyContent: "space-between",
                  }}
                  variant="dense"
                >
                  {program && displayProgram && (
                    <Box sx={{ display: "flex" }}>
                      <Typography sx={{ marginRight: 2 }} variant="subtitle1">
                        Program:
                      </Typography>
                      <Typography id="programName" variant="subtitle1">
                        {program.name}
                      </Typography>
                    </Box>
                  )}
                  {passage && (
                    <Box sx={{ display: "flex" }}>
                      <Typography sx={{ marginRight: 2 }} variant="subtitle1">
                        Passage:
                      </Typography>
                      <Typography id="passageName" variant="subtitle1">
                        {passage.name}
                      </Typography>
                    </Box>
                  )}
                  {item && (
                    <Box sx={{ display: "flex" }}>
                      <Typography sx={{ marginRight: 2 }} variant="subtitle1">
                        Item:
                      </Typography>
                      <Typography id="itemName" variant="subtitle1">
                        {item.name}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: "flex" }}>
                    {!!passage?.items?.length && (
                      <>
                        <ButtonGroup
                          aria-label="Item Set navigation"
                          color="inherit"
                          sx={{ marginRight: 2 }}
                          variant="outlined"
                        >
                          <Button
                            component={RouterLink}
                            disabled={disableSwitchItem}
                            to={{
                              pathname: `/workspace`,
                              search: getQueryString("previous"),
                              state: { from: location.state?.from },
                            }}
                            reloadDocument
                            size="small"
                          >
                            <ChevronLeftIcon />
                          </Button>
                          <Button
                            disabled={disableSwitchItem}
                            endIcon={<ChevronDownIcon />}
                            onClick={handleItemSetNavigationMenuClick}
                            size="small"
                          >
                            {item?.name || `Select Item`}
                          </Button>
                          <Button
                            component={RouterLink}
                            disabled={disableSwitchItem}
                            to={{
                              pathname: `/workspace`,
                              search: getQueryString("next"),
                              state: { from: location.state?.from },
                            }}
                            reloadDocument
                            size="small"
                          >
                            <ChevronRightIcon />
                          </Button>
                        </ButtonGroup>
                        <Menu
                          anchorEl={itemSetNavigationMenuAnchorEl}
                          onClose={handleItemSetNavigationMenuClose}
                          open={Boolean(itemSetNavigationMenuAnchorEl)}
                        >
                          {passage.items.map((i) => (
                            <MenuItem
                              color="inherit"
                              component={RouterLink}
                              disabled={
                                i.id === item?.id ||
                                !accessibleItems.some(({ id }) => id === i.id)
                              }
                              dense={true}
                              key={i.id}
                              onClick={handleItemSetNavigationMenuClose}
                              reloadDocument
                              to={{
                                pathname: `/workspace`,
                                reloadDocument: true,
                                search: `?itemID=${i.id}&passageID=${passageID}`,
                                state: { from: location.state?.from },
                              }}
                              underline="none"
                              variant="button"
                            >
                              {i.name}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                    <ButtonGroup color="primary" variant="contained">
                      <Button onClick={goBack} size="small">
                        Close
                      </Button>
                      <Button
                        aria-label="Workspace Save Button"
                        disabled={disableSave}
                        size="small"
                        type="submit"
                      >
                        Save
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Toolbar>
              </AppBar>
              <ResponsiveGridLayout
                breakpoints={{ lg: 1200 }}
                cols={{ lg: 36 }}
                draggableHandle=".MuiCardHeader-content"
                layouts={{ lg: workspace.cards }}
                onBreakpointChange={workspaceStore.onBreakpointChange}
                onLayoutChange={workspaceStore.onLayoutChange}
                onWidthChange={workspaceStore.onWidthChange}
                rowHeight={50}
                sx={{ overflowX: "hidden" }}
              >
                {workspace.cards?.map(({ i: key }) => {
                  const card = cards.find((o) => o.key === key);
                  return (
                    card?.component && (
                      <WorkspaceCard
                        close={card.canClose && workspaceStore.closeCard(key)}
                        key={key}
                        title={card.title}
                      >
                        {React.cloneElement(card.component, {
                          state: card.state,
                        })}
                      </WorkspaceCard>
                    )
                  );
                })}
              </ResponsiveGridLayout>
            </>
          )}
        </form>
        {saveBankedProps && <BankedItemSaveDialog {...saveBankedProps} />}
        {alertDialogProps.open && (
          <AlertDialog {...alertDialogProps}>
            The following issues were found:
            <List dense={true} disablePadding={true}>
              {saveIssues.map((issue) => {
                return <ListItem dense={true}>{issue}</ListItem>;
              })}
            </List>
          </AlertDialog>
        )}
        {warningDialogProps.open && <WarningDialog {...warningDialogProps} />}
      </FormProvider>
    </ReadyProvider>
  );
};

function flattenArray(inputArray) {
  let result = [];
  stepThruArray(0, inputArray, result);
  return result;
}

// react-hook-form's dirtyFields is a Proxy: Object.keys() on a nested object
// returns every field name in that object's shape, not just the dirty ones —
// each field's own value (true, or a nested object/array to recurse into) is
// what actually indicates dirtiness. See onSubmitBanked for the same pattern.
const hasDirtyLeaf = (node) => {
  if (node === true) return true;
  if (!node || typeof node !== "object") return false;
  return Object.values(node).some(hasDirtyLeaf);
};

const isOnlyMetafileAssetsDirty = (objectDirtyFields) => {
  if (!objectDirtyFields) return true;
  return Object.keys(objectDirtyFields)
    .filter((key) => key !== "metafileAssets")
    .every((key) => !hasDirtyLeaf(objectDirtyFields[key]));
};

// A non-admin Content Specialist is normally blocked from saving a Banked
// item/passage (see itemAdminAccess/passageAdminAccess), but may still save
// if the only thing THEY changed on THAT object is metafileAssets and they
// hold the action. Each object is checked against its own dirty fields, not
// the combined item+passage state — otherwise an unrelated, non-dirty
// item that happens to also qualify could unlock Save for a passage-only
// change that doesn't itself qualify (or vice versa), silently skipping the
// passage's own save (canSave still blocks it) while reporting success.
const hasMetafileOnlySaveOverride = ({
  item,
  passage,
  itemAdminAccess,
  passageAdminAccess,
  dirtyFields,
}) => {
  const itemOverride =
    itemAdminAccess &&
    isOnlyMetafileAssetsDirty(dirtyFields.item) &&
    item?._actions?.includes("manageMetafileAssets");
  const passageOverride =
    passageAdminAccess &&
    isOnlyMetafileAssetsDirty(dirtyFields.passage) &&
    passage?._actions?.includes("manageMetafileAssets");

  return { itemOverride, passageOverride };
};

const shouldDisableSave =
  ({ actions, readonly }) =>
  (dirtyFields) => {
    if (
      readonly &&
      !actions.includes("manageGraphicsAssets") &&
      !actions.includes("manageImageAssets") &&
      !actions.includes("manageMetafileAssets")
    )
      return true;

    let disableSaveButton = false;
    if (dirtyFields.item) {
      const changed = flattenArray(Object.values(dirtyFields.item));
      if (changed.length === 0) {
        disableSaveButton = true;
      } else {
        return false;
      }
    }
    if (dirtyFields.passage) {
      const changed = flattenArray(Object.values(dirtyFields.passage));
      if (changed.length === 0) {
        disableSaveButton = true;
      } else {
        disableSaveButton = false;
      }
    }
    return disableSaveButton;
  };

// *** recursive function ***
function stepThruArray(index, inputArray, result) {
  if (index >= inputArray.length) return;

  if (Array.isArray(inputArray[index])) {
    stepThruArray(0, inputArray[index], result);
  } else if (inputArray[index]?.constructor.name === "Object") {
    stepThruArray(0, Object.values(inputArray[index]), result);
  } else if (inputArray[index]) {
    result.push(inputArray[index]);
  }

  stepThruArray(index + 1, inputArray, result);
}

const preventUnload = (event) => {
  // Make sure that this event is coming from our origin (XSS)
  if (!event.origin || event.origin === window.location.origin) {
    event.preventDefault();
    event.returnValue =
      "You have not saved all changes.  Are you sure you want to exit?";
  }
};

export { preventUnload, Workspace };
